import React from "react";
import ReactDOM from "react-dom/client";
import { applyFirebaseAnalyticsDebugGlobal } from "./services/analytics/applyFirebaseAnalyticsDebugGlobal";
import { logDevAnalyticsConfigurationHint } from "./services/analytics/analyticsEnvironment";
import "./design/theme/tokens.css";
import "./design/theme/platformWeightPolicy.css";
import App from "./app/App";
import { ensureLatestAppShell } from "./features/pwa/ensureLatestAppShell";
import { registerPwaServiceWorker } from "./features/pwa";
import { markAppMounted, paintStartupFailure } from "./app/paintStartupFailure";
import { installFirestorePersistenceRecovery } from "./utils/firestorePersistenceRecovery";
import { withTimeout } from "./utils/withTimeout";

async function clearStaleDevServiceWorker() {
  if (process.env.NODE_ENV !== "development") return;
  if (!("serviceWorker" in navigator)) return;
  // RC2: never wipe SW/caches while offline — that guarantees blank reload.
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  try {
    // iOS can leave getRegistrations pending. Do not block first paint on it.
    await withTimeout(
      (async () => {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
        if ("caches" in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((key) => caches.delete(key)));
        }
      })(),
      2000
    );
  } catch {
    /* dev cleanup */
  }
}

async function bootstrap() {
  // Before React/Firestore traffic: auto-recover corrupted IndexedDB persistence.
  installFirestorePersistenceRecovery();

  applyFirebaseAnalyticsDebugGlobal();
  logDevAnalyticsConfigurationHint();

  await clearStaleDevServiceWorker();

  const reloading = await ensureLatestAppShell();
  if (reloading) return;

  if (process.env.NODE_ENV === "development") {
    import("./services/cmoe/shadow/registerDevDiagnostics").then((m) => {
      m.registerCmoeDevDiagnostics();
    });
  }

  const el = document.getElementById("root");
  if (!el) {
    throw new Error("Missing #root");
  }

  const root = ReactDOM.createRoot(el);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
  markAppMounted();

  const registerAfterPaint = () => registerPwaServiceWorker();
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(registerAfterPaint, { timeout: 5000 });
  } else {
    window.setTimeout(registerAfterPaint, 2000);
  }
}

bootstrap().catch((err) => {
  paintStartupFailure(err);
});
